// @ts-check
/**
 * @file recurrence.js
 * @location src/utils/recurrence.js
 * @role 반복 업무 생성 — 순수 함수만(생성 엔진). 클라(AUTO_GENERATE_RECURRING)·서버(pg_cron) 공용. 표시 라벨은 recurrenceLabels.js.
 *
 * dedupe 키 = `${templateId}@${occurrenceDate}@${memberId}` (멤버별 — Supabase UNIQUE(template_id,occurrence_date,aee) 대응).
 * 요일 정책: 매일=모든 요일(주말 포함). 평일만 원하면 「주1회/지정」에서 요일을 고른다. 월간은 지정일 그대로 발생(주말 보정 없음).
 */
import { STATUS, PRIORITY } from '../data/labels.js';
import { addDays, weekStart } from './dateUtils.js';
import { newId } from './ids.js';

/** ISO 요일 (월=1 .. 일=7) */
function isoWeekday(ymd) {
  const d = new Date(`${ymd}T00:00:00`).getDay(); // 0=일..6=토
  return d === 0 ? 7 : d;
}
function daysInMonth(y, m /* 1-based */) {
  return new Date(y, m, 0).getDate();
}
/** 그 달의 실제 발화일 (말일 clamp — 주말 보정 없음, 지정일 그대로). */
function monthlyFireDate(y, m, day) {
  const d = Math.min(day, daysInMonth(y, m));
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
/** 그 달 weekday(1=월..5=금)의 N번째(1..4) 또는 마지막('last') 발생일. 없으면 null(5 등). */
function nthWeekdayOfMonth(y, m /* 1-based */, weekday, nth) {
  const pad = (n) => String(n).padStart(2, '0');
  const dim = daysInMonth(y, m);
  if (nth === 'last') {
    for (let d = dim; d >= 1; d--) {
      const ymd = `${y}-${pad(m)}-${pad(d)}`;
      if (isoWeekday(ymd) === weekday) return ymd;
    }
    return null;
  }
  let count = 0;
  for (let d = 1; d <= dim; d++) {
    const ymd = `${y}-${pad(m)}-${pad(d)}`;
    if (isoWeekday(ymd) === weekday) {
      count++;
      if (count === nth) return ymd;
    }
  }
  return null;
}

/**
 * 해당 날짜가 반복 규칙의 발화일인지.
 * @param {import('../data/types.js').TaskRepeat} repeat
 * @param {string} ymd 'YYYY-MM-DD'
 * @returns {boolean}
 */
export function matchesRule(repeat, ymd) {
  switch (repeat?.freq) {
    case 'daily':
      return true;
    case 'weekly': {
      if (!(repeat.weekdays ?? []).includes(isoWeekday(ymd))) return false;
      const interval = repeat.interval ?? 1;
      if (interval <= 1 || !repeat.anchor) return true;     // 매주 (또는 앵커 없으면 안전 폴백)
      const wk = (s) => new Date(`${weekStart(new Date(`${s}T00:00:00`))}T00:00:00`).getTime();
      const weeks = Math.round((wk(ymd) - wk(repeat.anchor)) / (7 * 86400000));
      return weeks >= 0 && weeks % interval === 0;          // 앵커주=0 기준 N주 간격
    }
    case 'monthly': {
      const [y, m] = ymd.split('-').map(Number);
      if ('nth' in repeat) return ymd === nthWeekdayOfMonth(y, m, repeat.weekday ?? 1, repeat.nth ?? 1);
      return ymd === monthlyFireDate(y, m, repeat.day ?? 1);
    }
    default:
      return false;
  }
}

/**
 * (startExclusive, today] 구간의 발화일 목록. 최대 14일 백필 캡.
 * @param {import('../data/types.js').TaskRepeat} repeat
 * @param {string|null} startExclusive 'YYYY-MM-DD' — 이 날짜 다음부터 (보통 lastGenDate 또는 createdAt 날짜)
 * @param {string} today 'YYYY-MM-DD'
 * @returns {string[]}
 */
export function scheduledDatesIn(repeat, startExclusive, today) {
  const lower = addDays(today, -13);                 // 14일 캡 (today 포함 14일)
  let cursor = startExclusive ? addDays(startExclusive, 1) : lower;
  if (cursor < lower) cursor = lower;
  const out = [];
  let guard = 0;
  while (cursor <= today && guard++ < 400) {
    if (matchesRule(repeat, cursor)) out.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return out;
}

/**
 * 템플릿 + 발생일 + 멤버 → 신규 task. due=발생일(offset 폐기), priority=normal 기본.
 * @param {import('../data/types.js').TaskTemplate} t
 * @param {string} occurrenceDate 'YYYY-MM-DD'
 * @param {string} memberId 담당자 id (aees 중 1명)
 * @param {string} uid 매니저(지시자) id
 * @returns {import('../data/types.js').Task}
 */
export function buildTaskFromTemplate(t, occurrenceDate, memberId, uid) {
  const isSelf = memberId === uid;
  return {
    id: newId('task'),
    createdAt: new Date().toISOString(),
    title: t.title,
    status: /** @type {import('../data/types.js').TaskStatus} */ (STATUS.TODO),
    priority: /** @type {import('../data/types.js').TaskPriority} */ (PRIORITY.NORMAL),
    aee: memberId,
    aor: uid,
    due: occurrenceDate,
    dateKind: 'ondate',
    templateId: t.id,
    occurrenceDate,
    ...(isSelf ? { acknowledgedAt: new Date().toISOString() } : {}),
  };
}

/**
 * 활성 템플릿마다 미생성 발생분을 생성. 멱등 (templateId@occurrenceDate@memberId dedupe).
 * @param {import('../data/types.js').TaskTemplate[]} templates
 * @param {import('../data/types.js').Task[]} tasks  현재 tasks (dedupe 기준)
 * @param {string} today 'YYYY-MM-DD'
 * @param {string} uid 매니저 id
 * @returns {{ newTasks: import('../data/types.js').Task[], updatedTemplates: import('../data/types.js').TaskTemplate[] }}
 */
export function generateDueTasks(templates, tasks, today, uid) {
  // dedupe 키: 기존 task 는 t.templateId, 신규는 템플릿 원소(tmpl)의 id — 둘은 같은 값(template id).
  const existing = new Set(
    (tasks ?? []).filter(t => t.templateId).map(t => `${t.templateId}@${t.occurrenceDate}@${t.aee}`)
  );
  const newTasks = [];
  const updatedTemplates = (templates ?? []).map(tmpl => {
    if (!tmpl.active) return tmpl;
    // 안전판: lastGenDate·createdAt 둘 다 없으면 startExclusive=null 이 14일 백필을 유발 →
    // 어제(today-1)를 기준으로 두어 오늘분만 생성 (멤버 N명 × 14일 중복 폭발 방지).
    const start = tmpl.lastGenDate || (tmpl.createdAt ? tmpl.createdAt.slice(0, 10) : addDays(today, -1));
    const dates = scheduledDatesIn(tmpl.repeat, start, today);
    for (const d of dates) {
      for (const memberId of (tmpl.aees ?? [])) {
        const key = `${tmpl.id}@${d}@${memberId}`;
        if (existing.has(key)) continue;
        existing.add(key);
        newTasks.push(buildTaskFromTemplate(tmpl, d, memberId, uid));
      }
    }
    return { ...tmpl, lastGenDate: today };
  });
  return { newTasks, updatedTemplates };
}

